import {
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit'
import type { RootState } from '@/store/types'
import { localStorageFields } from '@/constants/localStorage'

const FIELD_NAME = localStorageFields.currency

export type CurrencyCode = 'USD' | 'HKD' | 'CNY'

export interface ICurrencyMeta {
  code: CurrencyCode
  label: string
  symbol: string
  // Conversion rate relative to USD.
  rate: number
}

// Single source of truth for the currency switcher.
export const currencyOptions = [
  { code: 'USD', label: 'USD', symbol: '$', rate: 1 },
  { code: 'HKD', label: 'HKD', symbol: '', rate: 8 },
  { code: 'CNY', label: 'CNY', symbol: '¥', rate: 7 }
] as const

export const getCurrencyMeta = (code: CurrencyCode): ICurrencyMeta =>
  currencyOptions.find(currency => currency.code === code) || currencyOptions[0]

// Display prefix for a currency: its symbol, falling back to the label when
// the currency has no symbol.
export const getCurrencySymbol = (code: CurrencyCode): string => {
  const meta = getCurrencyMeta(code)
  return meta.symbol || meta.label
}

export interface ICurrency {
  currency: CurrencyCode
}

// The currency switcher is hidden while only USD is supported, so a value
// stored by an earlier session must not leak into prices.
const initialState: ICurrency = {
  currency: 'USD'
}

const currency = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    setCurrency: (state, { payload }: PayloadAction<CurrencyCode>) => {
      state.currency = payload
      localStorage.setItem(FIELD_NAME, payload)
    }
  }
})

export const { setCurrency } = currency.actions
export const getCurrency = (state: RootState) => state.currency.currency

export default currency.reducer
